/**
 * *Attached to* — one field, one search (`docs/activity.md` §9.2, Board 11 C;
 * Aaron, Oct 24, notes 1.1 and 1.2).
 *
 * Aaron: "rename the with and where search bar to Attached To and remove the
 * attached to at the bottom" — and "This new search bar that includes
 * creating properties and contacts and searches across everything, should be
 * the attached to bar in every activity creation." So this is New request's
 * search (`requests/search-box.tsx`, the box he called brilliant) with the
 * chips of what is attached inside it: contacts and properties as New request
 * finds them — a contact with one place on its row, a place with its people —
 * then the jobs, visits, invoices and equipment an activity can be attached
 * to (`GET /activity/find?records=1`), then the addresses Google knows that
 * are not on file (through our own `/places/*`, as `AddressAutocomplete`
 * asks), then *New contact* for a name nobody has.
 *
 * What comes along with a pick is the server's (`GET /activity/attach/suggest`),
 * marked *auto*; anything already there is not added twice; a removed chip
 * stays removed (the sheets send `expand: false` with exactly these). A pick
 * also says what it implies for a meeting — `onFound`: a place and its
 * contact, a job's place and contact, a contact's places when they have
 * several (small question 1) — which the meeting sheet turns into *Where* and
 * *With*. An address not on file offers **New property and contact** (the
 * real `PropertyForm`, the address already in it, its *Primary contact*
 * lookup making the contact) or, where there is a *Where*, **Just use the
 * address**.
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react'
import { can, firstNameOf, parseTyped, type MeResponse, type NewSetup } from '@rowted/shared'
import { uuidv7 } from 'uuidv7'
import {
  Highlight, SearchBox, type SearchChoice, type SearchGroup,
} from '../../requests/search-box.js'
import type { FindResult, FoundPlace } from '../../requests/api.js'
import { placesAvailable, type ChosenAddress } from '../../components/form/address-autocomplete.js'
import { ContactForm } from '../../components/forms/contact-form.js'
import { PropertyForm } from '../../components/forms/property-form.js'
import { request } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { activityRequest } from '../api.js'
import { ActivityContext } from '../activity-context.js'
import { PICKER_LAYER } from './kit.js'

/** Every kind `activity_links` holds. */
export type AttachType = 'job' | 'contact' | 'property' | 'visit' | 'invoice' | 'equipment'

export interface AttachChip {
  type: AttachType
  id: string
  label: string
  /** Came along with a pick (§9.2). */
  auto: boolean
}

/** What a conversation can be attached to (`conversation_links`, 0134): a text's and an email's *Attached to*. */
export const CONVERSATION_KINDS: readonly AttachType[] = ['contact', 'property', 'job']

/** What a pick implies for a meeting's *Where* and *With* (Aaron, Oct 24, note 1.1.3). */
export interface AttachFound {
  contact: { id: string; name: string } | null
  property: { id: string; name: string } | null
  /** A contact's places when there are several, for *Where* to offer (small question 1). */
  places?: { id: string; name: string; line1: string; city: string }[]
  /** An address that is not on file, used as it is (note 1.1.4). */
  address?: ChosenAddress | null
}

interface RecordHit { type: AttachType; id: string; label: string; sub: string }
interface GooglePlace { id: string; name: string; sub: string | null }

/** `/activity/find?records=1`, and Google's addresses beside it. */
type AttachFindResult = FindResult & {
  records?: { jobs: RecordHit[]; visits: RecordHit[]; invoices: RecordHit[]; equipment: RecordHit[] }
  addresses?: GooglePlace[]
}

type ExtraValue = { kind: 'record'; hit: RecordHit } | { kind: 'address'; place: GooglePlace }

const GLYPH: Record<AttachType, GlyphName> = {
  contact: 'user', property: 'pin', job: 'layers', visit: 'calendar', invoice: 'file', equipment: 'truck',
}

/** A record's tile in the results, the kind's own colours. */
const TILE: Record<Exclude<AttachType, 'contact' | 'property'>, string> = {
  job: 'border-teal-band-line bg-teal-band-bg text-teal-band-text',
  visit: 'border-amber-band-line bg-amber-band-bg text-amber-band-text',
  invoice: 'border-info-line bg-info-bg text-info',
  equipment: 'border-line bg-line-soft text-text-muted',
}

/** The signed-in person, asked once, for a sheet opened outside Activity (a record's *Email* and *Text*). */
let meAsked: Promise<MeResponse | null> | null = null

/**
 * Whether this person may make a contact and a property from the search —
 * Activity's own `me` on the Activity page; `/me`, once, anywhere else, since
 * a record page opens New email and New text with no Activity around them
 * (Aaron, Oct 23: "send from every record").
 */
function useMayMake(): { contacts: boolean; properties: boolean } {
  const activity = useContext(ActivityContext)
  const [me, setMe] = useState<MeResponse | null>(activity?.me ?? null)
  useEffect(() => {
    if (activity) return undefined
    let live = true
    meAsked ??= request<MeResponse>('/me').catch(() => null)
    void meAsked.then((answer) => {
      if (!answer) meAsked = null
      if (live) setMe(answer)
    })
    return () => { live = false }
  }, [activity])
  const permissions = activity?.me.permissions ?? me?.permissions
  return {
    contacts: permissions ? can(permissions, 'contacts', 'edit') : false,
    properties: permissions ? can(permissions, 'properties', 'edit') : false,
  }
}

/** What a pick brings with it, from the server (§9.2). */
export async function suggestFor(type: AttachType, id: string): Promise<AttachChip[]> {
  try {
    const answer = await activityRequest<{ items: AttachChip[] }>(`/activity/attach/suggest?type=${type}&id=${id}`)
    return answer.items.map((item) => ({ ...item, auto: true }))
  } catch {
    return []
  }
}

/** `chips` with `more` added where they are not there already. */
export function withChips(chips: readonly AttachChip[], more: readonly AttachChip[]): AttachChip[] {
  const out = [...chips]
  for (const chip of more) {
    if (!out.some((one) => one.type === chip.type && one.id === chip.id)) out.push(chip)
  }
  return out
}

const same = (a: AttachChip, b: { type: string; id: string }) => a.type === b.type && a.id === b.id

/**
 * *418 Orchard Ridge Drive* and *418 Orchard Ridge Dr* are one street line:
 * Google writes the suffix out, a property is saved with it short, so an
 * address already on file is not offered again as one that is not.
 */
const SUFFIXES: Record<string, string> = {
  street: 'st', road: 'rd', drive: 'dr', avenue: 'ave', lane: 'ln', court: 'ct', boulevard: 'blvd',
  place: 'pl', circle: 'cir', terrace: 'ter', parkway: 'pkwy', highway: 'hwy', trail: 'trl', ridge: 'rdg',
  square: 'sq', crossing: 'xing', hollow: 'holw',
}
const streetKey = (line: string): string => line.toLowerCase().replace(/[.,]/g, ' ').split(/\s+/).filter(Boolean)
  .map((word) => SUFFIXES[word] ?? word).join(' ')

function Chip({ chip, onRemove }: { chip: AttachChip; onRemove: () => void }) {
  return (
    <span data-chip className="inline-flex h-7 items-center gap-[5px] whitespace-nowrap rounded-lg border border-accent-line bg-green-band-bg pl-[9px] pr-1.5 text-[12px] font-semibold">
      <Glyph name={GLYPH[chip.type]} size={12} />
      {chip.label}
      {chip.auto && <span className="ml-0.5 text-[10px] font-semibold text-accent-dark">auto</span>}
      <button type="button" aria-label={`Remove ${chip.label}`} onClick={onRemove}
        className="inline-flex text-text-muted hover:text-text">
        <Glyph name="close" size={12} />
      </button>
    </span>
  )
}

/** A result row's tile — the same size as the avatars and place marks beside it. */
function Tile({ glyph, className, phone }: { glyph: GlyphName; className: string; phone: boolean }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center rounded-tile-small border', className,
      phone ? 'h-8 w-8' : 'h-[26px] w-[26px]')}>
      <Glyph name={glyph} size={phone ? 16 : 14} />
    </span>
  )
}

function RowText({ title, sub, words }: { title: string; sub: string | null; words: string[] }) {
  return (
    <span className="flex min-w-0 flex-1 flex-col leading-tight">
      <b className="truncate text-[13px] font-semibold text-text"><Highlight text={title} words={words} /></b>
      {sub && <span className="truncate text-[11.5px] text-text-muted"><Highlight text={sub} words={words} /></span>}
    </span>
  )
}

const RECORD_GROUPS: { key: 'jobs' | 'visits' | 'invoices' | 'equipment'; title: string }[] = [
  { key: 'jobs', title: 'Jobs' },
  { key: 'visits', title: 'Visits' },
  { key: 'invoices', title: 'Invoices' },
  { key: 'equipment', title: 'Equipment' },
]

/** An address Google found that is not on file: make the property, or (with a *Where*) just use it. */
function AddressOffer({
  address, phone, canMake, canUse, onMake, onUse, onDismiss,
}: {
  address: ChosenAddress; phone: boolean; canMake: boolean; canUse: boolean
  onMake: () => void; onUse: () => void; onDismiss: () => void
}) {
  const line = [address.line1, address.city, [address.state, address.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ')
  return (
    <div role="group" aria-label="An address not on file"
      className="mt-1.5 rounded-[11px] border border-line bg-surface-alt px-3 py-2.5">
      <div className="flex items-start gap-2.5">
        <Tile glyph="pin" className="border-accent-line bg-accent-soft text-accent-dark" phone={phone} />
        <span className="flex min-w-0 flex-1 flex-col leading-tight">
          <b className="truncate text-[13px] font-semibold">{line}</b>
          <span className="text-[11.5px] text-text-muted">not on file — from Google</span>
        </span>
        <button type="button" aria-label="Not this address" onClick={onDismiss}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-hover">
          <Glyph name="close" size={13} />
        </button>
      </div>
      <div className={cn('mt-2 flex gap-2', phone && 'flex-col')}>
        {canMake && (
          <button type="button" onClick={onMake}
            className={cn('inline-flex items-center justify-center gap-1.5 rounded-lg border border-accent bg-accent px-3 font-semibold text-white hover:bg-accent-dark',
              phone ? 'h-11 text-[15px]' : 'h-8 text-[12.5px]')}>
            <Glyph name="plus" size={14} stroke={2.2} />New property and contact
          </button>
        )}
        {canUse && (
          <button type="button" onClick={onUse}
            className={cn('inline-flex items-center justify-center rounded-lg border border-line bg-surface px-3 font-semibold text-text hover:bg-hover',
              phone ? 'h-11 text-[15px]' : 'h-8 text-[12.5px]')}>
            Just use the address
          </button>
        )}
      </div>
    </div>
  )
}

export function AttachedTo({
  chips, onChange, phone, required, hint, id, compact = false, onFound, useAddress = false, inputRef, label,
  kinds,
}: {
  chips: AttachChip[]
  onChange: (chips: AttachChip[], picked?: { chip: AttachChip; brought: AttachChip[] }) => void
  phone: boolean
  required: boolean
  hint?: ReactNode
  id?: string
  /** 36 px, for the calendar's quick card. */
  compact?: boolean
  /** What a pick implies for *Where* and *With* — the meeting sheet's and the quick card's. */
  onFound?: (found: AttachFound) => void
  /** An address not on file may be used as it is — only where there is a *Where* to put it in. */
  useAddress?: boolean
  inputRef?: RefObject<HTMLInputElement>
  /** The field's own label row; the quick card draws its own. */
  label?: false
  /** Only these kinds — a text and an email are a conversation's, which holds contacts, places and jobs. */
  kinds?: readonly AttachType[]
}) {
  const may = useMayMake()
  const mayAddContacts = may.contacts
  const mayAddProperties = may.properties
  /* Google bills a run of typing and the details call that ends it once, when they share a token. */
  const session = useRef<string | null>(null)
  const [offer, setOffer] = useState<ChosenAddress | null>(null)
  const [making, setMaking] = useState<
    | { kind: 'property'; address: ChosenAddress; orgUnitId: string | undefined }
    | { kind: 'contact'; name: string; phone?: string; email?: string; orgUnitId: string } | null>(null)

  /* Only where an address could become something: a property, or a meeting's *Where*. */
  const asksGoogle = mayAddProperties || useAddress
  const find = useCallback(async (text: string, limit: number, signal?: AbortSignal): Promise<FindResult> => {
    const query = new URLSearchParams({ q: text, limit: String(limit), records: '1' })
    const street = asksGoogle && text.trim().length >= 3 && parseTyped(text).street !== null
    const [found, addresses] = await Promise.all([
      activityRequest<AttachFindResult>(`/activity/find?${query.toString()}`, { signal }),
      street ? placesAvailable().then((ok) => {
        if (!ok) return [] as GooglePlace[]
        session.current ??= uuidv7()
        return request<{ items: GooglePlace[] }>(
          `/places/suggest?q=${encodeURIComponent(text.trim())}&session=${session.current}`, { signal })
          .then((answer) => answer.items).catch(() => [] as GooglePlace[])
      }) : Promise.resolve([] as GooglePlace[]),
    ])
    /* An address already on file is its property's row, not a second one from Google. */
    const onFile = new Set(found.properties.items.map((place) => streetKey(place.line1)))
    return { ...found, addresses: addresses.filter((place) => !onFile.has(streetKey(place.name))).slice(0, 3) } as FindResult
  }, [asksGoogle])

  const extra = useCallback((found: FindResult, words: string[]): SearchGroup[] => {
    const answer = found as AttachFindResult
    const groups: SearchGroup[] = []
    for (const group of RECORD_GROUPS) {
      const hits = (answer.records?.[group.key] ?? []).filter((hit) => !kinds || kinds.includes(hit.type))
      if (hits.length === 0) continue
      groups.push({
        key: group.key,
        title: `${group.title} · ${hits.length}`,
        rows: hits.map((hit) => ({
          key: hit.id,
          value: { kind: 'record', hit } satisfies ExtraValue,
          node: (
            <>
              <Tile glyph={GLYPH[hit.type]} className={TILE[hit.type as keyof typeof TILE]} phone={phone} />
              <RowText title={hit.label} sub={hit.sub} words={words} />
              {(hit.type === 'job' || hit.type === 'visit') && !compact && (
                <span className="whitespace-nowrap text-[11px] font-semibold text-accent-dark">+ contact &amp; property</span>
              )}
            </>
          ),
        })),
      })
    }
    const addresses = answer.addresses ?? []
    if (addresses.length > 0) {
      groups.push({
        key: 'addresses',
        title: `Addresses · ${addresses.length}`,
        rows: addresses.map((place) => ({
          key: place.id,
          value: { kind: 'address', place } satisfies ExtraValue,
          node: (
            <>
              <Tile glyph="pin" className="border-line bg-surface text-text-muted" phone={phone} />
              <RowText title={place.name} sub={place.sub ? `${place.sub} · not on file` : 'not on file'} words={words} />
            </>
          ),
        })),
      })
    }
    return groups
  }, [phone, compact, kinds])

  /**
   * Puts `first` (and anything named with it) on, then what the server says
   * comes along; answers everything that comes with it, on already or not,
   * because that is what *Where* and *With* follow.
   */
  const attach = async (first: AttachChip, also: AttachChip[] = []): Promise<AttachChip[]> => {
    /* A chip that came along and is now picked on purpose stops being *auto*. */
    let base = chips.some((one) => same(one, first))
      ? chips.map((one) => (same(one, first) ? { ...one, auto: false } : one))
      : [...chips, first]
    base = withChips(base, also)
    const suggested = await suggestFor(first.type, first.id)
    const brought = suggested
      .filter((one) => !base.some((had) => same(had, one)))
      /* A job's contact comes before its property (Boards 11 E, G); a contact's property before the job (11 B). */
      .sort((a, b) => (first.type === 'job' ? (a.type === 'contact' ? -1 : 0) - (b.type === 'contact' ? -1 : 0) : 0))
    onChange(withChips(base, brought), { chip: first, brought })
    return suggested
  }

  /** What a pick implies for *Where* and *With*: what the row named, else what came along. */
  const found = (named: { contact?: { id: string; name: string } | null; property?: { id: string; name: string } | null },
    brought: AttachChip[], places?: FoundPlace[]) => {
    if (!onFound) return
    const contact = named.contact ?? brought.find((chip) => chip.type === 'contact') ?? null
    const property = named.property ?? brought.find((chip) => chip.type === 'property') ?? null
    onFound({
      contact: contact ? { id: contact.id, name: 'name' in contact ? contact.name : contact.label } : null,
      property: property ? { id: property.id, name: 'name' in property ? property.name : property.label } : null,
      ...(places && places.length > 1 && !property
        ? { places: places.map((place) => ({ id: place.id, name: place.name, line1: place.line1, city: place.city })) }
        : {}),
    })
  }

  const officeUnit = async (): Promise<string | undefined> =>
    activityRequest<NewSetup>('/activity/new-setup').then((setup) => setup.office?.orgUnitId).catch(() => undefined)

  const choose = async (choice: SearchChoice) => {
    setOffer(null)
    if (choice.kind === 'contact') {
      const person = choice.picked?.contact
      const place = choice.picked?.property
      const chip: AttachChip = { type: 'contact', id: choice.contactId, label: person?.name ?? '', auto: false }
      /* A place picked under a person is picked; a contact's only place comes along, marked *auto* (Board 11 B). */
      const also: AttachChip[] = place && choice.propertyId ? [{ type: 'property', id: place.id, label: place.name, auto: false }] : []
      const brought = await attach(chip, also)
      found({ contact: person ?? null, property: place ?? null }, brought, choice.places)
      return
    }
    if (choice.kind === 'property') {
      const place = choice.picked?.property
      const person = choice.picked?.contact
      const chip: AttachChip = { type: 'property', id: choice.propertyId, label: place?.name ?? '', auto: false }
      /* A person picked under a place is picked; a place's own primary contact comes along. */
      const also: AttachChip[] = person && choice.contactId ? [{ type: 'contact', id: person.id, label: person.name, auto: false }] : []
      const brought = await attach(chip, also)
      found({ property: place ?? null, contact: person ?? null }, brought)
      return
    }
    if (choice.kind === 'new-contact') {
      const parsed = choice.parsed
      setMaking({
        kind: 'contact', name: [parsed.firstName, parsed.lastName].filter(Boolean).join(' '),
        phone: parsed.phone ?? undefined, email: parsed.email ?? undefined, orgUnitId: (await officeUnit()) ?? '',
      })
    }
  }

  /** A row of the groups this field adds: a job, a visit, an invoice, a machine, or one of Google's addresses. */
  const chooseExtra = async (value: ExtraValue) => {
    setOffer(null)
    if (value.kind === 'record') {
      const hit = value.hit
      /* A job's chip is its number (Board 11 D, F: *J-3319*); a visit keeps its day. */
      const chip: AttachChip = { type: hit.type, id: hit.id, label: hit.type === 'job' ? hit.label.split(' · ')[0]! : hit.label, auto: false }
      const brought = await attach(chip)
      if (hit.type !== 'equipment') found({}, brought)
      return
    }
    const token = session.current ?? uuidv7()
    session.current = null
    const details = await request<{ address: ChosenAddress | null }>(
      `/places/${encodeURIComponent(value.place.id)}?session=${token}`).catch(() => ({ address: null }))
    setOffer(details.address ?? { line1: value.place.name, city: '', state: '', postalCode: '', latitude: null, longitude: null })
  }

  /** A property made from the address: on, with whoever its form made its contact. */
  const madeProperty = async (propertyId: string) => {
    setMaking(null)
    const place = await activityRequest<{ id: string; name: string }>(`/activity/place?propertyId=${propertyId}`)
      .catch(() => ({ id: propertyId, name: '' }))
    const brought = (await attach({ type: 'property', id: propertyId, label: place.name, auto: false }))
    found({ property: { id: propertyId, name: place.name } }, brought)
  }

  const madeContact = async (contactId: string) => {
    setMaking(null)
    const person = await activityRequest<{ id: string; name: string }>(`/activity/compose?contactId=${contactId}`)
      .catch(() => ({ id: contactId, name: '' }))
    const brought = await attach({ type: 'contact', id: contactId, label: person.name, auto: false })
    found({ contact: { id: contactId, name: person.name } }, brought)
  }

  const lead = useMemo(() => chips.map((chip) => (
    <Chip key={`${chip.type}-${chip.id}`} chip={chip}
      onRemove={() => onChange(chips.filter((one) => !same(one, chip)))} />
  )), [chips, onChange])

  return (
    <div className="min-w-0">
      {label !== false && (
        <div className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
          <span id={id}>Attached to</span>
          {required ? <i aria-hidden className="inline-block h-[5px] w-[5px] rounded-full bg-accent" />
            : <span className="ml-auto text-text-faint">optional</span>}
        </div>
      )}
      <div role="group" aria-labelledby={label !== false ? id : undefined} aria-label={label === false ? 'Attached to' : undefined}>
        <SearchBox phone={phone} compact={compact} inline find={find} extra={extra} inputRef={inputRef}
          maxHeight={compact ? 330 : undefined}
          canAddContacts={mayAddContacts} offerStreet={false} newContactOnlyAlone
          newContactEnter="opens a new contact with that filled in"
          label="Find a contact, property or job" placeholder={phone ? 'Add…' : 'Add a contact, property or job…'}
          lead={lead.length > 0 ? lead : undefined}
          onBackspace={() => { const last = chips[chips.length - 1]; if (last) onChange(chips.slice(0, -1)) }}
          footnote={compact ? undefined
            : 'Picking a job brings its contact and property with it; picking a contact brings the property and job if there is only one of each.'}
          onChoose={(choice) => void choose(choice)} onExtra={(value) => void chooseExtra(value as ExtraValue)} />
        {offer && (
          <AddressOffer address={offer} phone={phone} canMake={mayAddProperties} canUse={useAddress && Boolean(onFound)}
            onDismiss={() => setOffer(null)}
            onUse={() => { onFound?.({ contact: null, property: null, address: offer }); setOffer(null) }}
            onMake={() => {
              const address = offer
              setOffer(null)
              void officeUnit().then((orgUnitId) => setMaking({ kind: 'property', address, orgUnitId }))
            }} />
        )}
      </div>
      {hint && (
        <div className={cn('leading-[1.4] text-text-faint', phone ? 'mt-[5px] text-[12px]' : 'mt-1 text-[11px]')}>{hint}</div>
      )}
      {making?.kind === 'property' && (
        <PropertyForm open layer={PICKER_LAYER} orgUnitId={making.orgUnitId} initialAddress={making.address}
          onClose={() => setMaking(null)} onSaved={(propertyId) => void madeProperty(propertyId)}
          onOpenExisting={(propertyId) => void madeProperty(propertyId)} />
      )}
      {making?.kind === 'contact' && (
        <ContactForm open layer={PICKER_LAYER} orgUnitId={making.orgUnitId} initialName={making.name}
          initialPhone={making.phone} initialEmail={making.email}
          onClose={() => setMaking(null)} onSaved={(contactId) => void madeContact(contactId)}
          onOpenExisting={(contactId) => void madeContact(contactId)} />
      )}
    </div>
  )
}

/** *the property and the open job came with Renee — remove either if this isn’t about them* (Board 11 B). */
export function cameWithWords(picked: { chip: AttachChip; brought: AttachChip[] } | null): string | null {
  if (!picked || picked.chip.type !== 'contact' || picked.brought.length === 0) return null
  const parts = picked.brought.map((chip) => (chip.type === 'property' ? 'the property' : chip.type === 'job' ? 'the open job' : chip.label))
  const what = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0]
  return `${what} came with ${firstNameOf(picked.chip.label)} — remove ${parts.length > 1 ? 'either' : 'it'} if this isn’t about them`
}
